import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AlertOctagon, Phone, X, MessageSquare, Bot } from 'lucide-react';

export default function SOSWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Only show for authenticated users (civili can SOS; operators always have admin tools)
  if (!user || user === false) return null;

  return (
    <>
      {/* Floating SOS button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="sos-widget-trigger"
        title="SOS Emergenza"
        className="fixed top-20 right-4 z-40 w-14 h-14 bg-[#FF3B30] hover:bg-[#FF1A1A] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
        style={{ boxShadow: '0 6px 20px rgba(255,59,48,0.55)' }}
      >
        <div className="relative">
          <AlertOctagon size={26} strokeWidth={2.5} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-pulse" />
        </div>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="sos-widget-modal"
        >
          <div
            className="bg-white border-2 border-[#FF3B30] max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#FF3B30] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertOctagon size={22} strokeWidth={2.5} />
                <span className="font-['Barlow_Condensed'] text-xl font-black uppercase tracking-tight">SOS Emergenza</span>
              </div>
              <button onClick={() => setOpen(false)} className="hover:opacity-80" data-testid="sos-close-btn">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <p className="text-sm text-[#0D1B2A] font-bold">Sei in pericolo? Scegli un&apos;azione:</p>

              <a
                href="tel:112"
                className="flex items-center justify-between p-4 bg-[#FF3B30] text-white hover:bg-[#FF1A1A] transition-all group"
                data-testid="sos-call-112"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white text-[#FF3B30] flex items-center justify-center group-hover:scale-110 transition-all">
                    <Phone size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-['Barlow_Condensed'] text-2xl font-black tracking-tight leading-none">112</div>
                    <div className="text-[10px] uppercase tracking-widest opacity-90">Numero unico emergenze</div>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest font-bold">Chiama</span>
              </a>

              <a
                href="tel:118"
                className="flex items-center justify-between p-3 border-2 border-[#0A84FF] hover:bg-[#0A84FF]/5 transition-all"
                data-testid="sos-call-118"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#0A84FF] text-white flex items-center justify-center">
                    <Phone size={16} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-['Barlow_Condensed'] text-xl font-black tracking-tight text-[#0A84FF] leading-none">118</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4A6B8A]">Emergenza sanitaria</div>
                  </div>
                </div>
              </a>

              <a
                href="tel:115"
                className="flex items-center justify-between p-3 border-2 border-[#FF9F0A] hover:bg-[#FF9F0A]/5 transition-all"
                data-testid="sos-call-115"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#FF9F0A] text-white flex items-center justify-center">
                    <Phone size={16} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-['Barlow_Condensed'] text-xl font-black tracking-tight text-[#FF9F0A] leading-none">115</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4A6B8A]">Vigili del fuoco</div>
                  </div>
                </div>
              </a>

              <div className="border-t border-[#E8F4FD] pt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setOpen(false); navigate('/'); window.scrollTo(0, 0); }}
                  className="flex items-center justify-center gap-2 p-3 bg-[#0D1B2A] text-white hover:bg-[#1A2D44] transition-all"
                  data-testid="sos-quick-request"
                >
                  <MessageSquare size={14} />
                  <span className="text-xs font-bold uppercase tracking-widest">Crea Segnalazione</span>
                </button>
                <button
                  onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('open-chatbot-triage')); }}
                  className="flex items-center justify-center gap-2 p-3 border-2 border-[#0A84FF] text-[#0A84FF] hover:bg-[#0A84FF] hover:text-white transition-all"
                  data-testid="sos-chatbot-trigger"
                >
                  <Bot size={14} />
                  <span className="text-xs font-bold uppercase tracking-widest">Assistente AI</span>
                </button>
              </div>

              <p className="text-[10px] text-[#6B8AB5] text-center pt-2 leading-relaxed">
                In caso di pericolo immediato per la vita, chiama prima il <strong className="text-[#FF3B30]">112</strong>.
                LIFELINKE collabora con i servizi di emergenza, ma non li sostituisce.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
